import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND_COLORS } from "@/lib/menuData";
import Footer from "@/components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BRAND_COLORS.sandWhite }}>
      {/* Header */}
      <header className="py-4 px-6" style={{ backgroundColor: BRAND_COLORS.deepTeal }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-white">
            <Shield className="h-5 w-5" />
            <span className="font-semibold text-sm">Privacy Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 py-8 px-6">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm p-8">
          <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Montserrat", color: BRAND_COLORS.darkSlate }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mb-6">Last Updated: February 2026</p>

          <div className="prose prose-sm max-w-none" style={{ color: BRAND_COLORS.darkSlate }}>
            <h2 className="text-lg font-semibold mt-6 mb-3">1. Introduction</h2>
            <p className="mb-4">
              Welcome to Dauphin Island Food Mart ("we," "our," or "us"). We are committed to protecting your privacy and 
              ensuring you have a positive experience on our website and when using our online ordering services.
            </p>
            <p className="mb-4">
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
              our website or use our online ordering platform.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">2. Information We Collect</h2>
            <h3 className="text-base font-medium mt-4 mb-2">Personal Information</h3>
            <p className="mb-4">When you place an order or create an account, we may collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Order history and preferences</li>
            </ul>

            <h3 className="text-base font-medium mt-4 mb-2">Automatically Collected Information</h3>
            <p className="mb-4">When you visit our website, we may automatically collect:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Device information (browser type, operating system)</li>
              <li>IP address</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website</li>
            </ul>

            <h2 className="text-lg font-semibold mt-6 mb-3">3. How We Use Your Information</h2>
            <p className="mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Process and fulfill your orders</li>
              <li>Send order confirmations and status updates</li>
              <li>Communicate with you about your orders</li>
              <li>Improve our website and services</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-lg font-semibold mt-6 mb-3">4. Cookies and Tracking Technologies</h2>
            <p className="mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. 
              Cookies are small data files stored on your device that help us remember your preferences 
              and understand how you use our website.
            </p>
            <p className="mb-4">
              You can control cookies through your browser settings. However, disabling cookies may limit 
              your ability to use certain features of our website.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">5. Third-Party Services</h2>
            <p className="mb-4">We may use third-party services for:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li><strong>Google Sign-In:</strong> For convenient account authentication</li>
              <li><strong>Email Services (Resend):</strong> To send order notifications</li>
              <li><strong>Web Push Notifications:</strong> To notify you when your order is ready</li>
              <li><strong>SMS Services:</strong> For order status updates (optional)</li>
            </ul>
            <p className="mb-4">
              These services have their own privacy policies that govern their use of your information.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">6. Data Security</h2>
            <p className="mb-4">
              We implement appropriate technical and organizational measures to protect your personal information 
              against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission 
              over the Internet is 100% secure.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">7. Your Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-1">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of marketing communications</li>
              <li>Disable push notifications through your browser settings</li>
            </ul>

            <h2 className="text-lg font-semibold mt-6 mb-3">8. Children's Privacy</h2>
            <p className="mb-4">
              Our website is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">9. Changes to This Policy</h2>
            <p className="mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>

            <h2 className="text-lg font-semibold mt-6 mb-3">10. Contact Us</h2>
            <p className="mb-4">
              If you have questions about this Privacy Policy or our privacy practices, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="font-semibold">Dauphin Island Food Mart</p>
              <p>1000 Bienville Blvd</p>
              <p>Dauphin Island, AL 36528</p>
              <p className="mt-2">Phone: (251) 861-2320</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
